import React, { useRef, useState, useEffect } from 'react';
import TextArea from '../Components/CoreComponents/textarea';
import { FaPaperPlane, FaCamera, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaDownload } from 'react-icons/fa';
import { StartChatResponse } from '../../Model/Response/ClientUser/StartChatResponse';
import { useLocation } from 'react-router-dom';
import { SetSignalRSessionToChatSession } from '../../Controller/Api/ChatClientUserController';
import { SetSignalRSessionToChatSessionRequest } from '../../Model/Request/ClientUser/SetSignalRSessionToChatSessionRequest';
import { SetSignalRSessionToChatSessionResponse } from '../../Model/Response/ClientUser/SetSignalRSessionToChatSessionResponse';
import { ApiBaseResponse, ResponseCode } from '../../Model/Response/Common/ApiBaseResponse';
import * as signalR from "@microsoft/signalr";
import { GlobalOperations } from '../../Controller/Common/GlobalOperations';
import { SendMessageByAgentResponse } from '../../Model/Response/SignalR/SendMessageByAgentResponse';
import { SendMessageByClientUserResponse } from '../../Model/Response/SignalR/SendMessageByClientUserResponse';
import { SendMessageByClientUserRequest } from '../../Model/Request/SignalR/SendMessageByClientUserRequest';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ChangeAgentTypingResponse } from '../../Model/Response/SignalR/ChangeAgentTypingResponse';
import FileUploadComponent from '../Components/CoreComponents/FileUploadComponent';
import { FileUploadResponse } from '../../Model/Response/ClientUser/FileUploadResponse';
import { renderToString } from 'react-dom/server'; // JSX'i string'e çevirmek için
import renderMessageContent from './renderMessageContent';
import RenderMessageContent from './renderMessageContent';

export interface ClientSideMessages {
    messageContent: string;
    messageOwnerName: string;
    messageDateUtc: number;
    messageSide: MessageSideEnum;
}

export enum MessageSideEnum {
    ClientUser = 1,
    Agent = 2
}

interface ChatScreenProps {
    chatStart: StartChatResponse | null;
}

const ChatScreen: React.FC<ChatScreenProps> = ({ chatStart }) => {
    const messageRef = useRef<any>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [clientUserName, setClientUserName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [showExportPdf, setShowExportPdf] = useState<boolean>(false);
    const [sessionId, setSessionId] = useState<number>(0);
    const chatContentRef = useRef<HTMLDivElement>(null); // chat alanı referansı
    const [messages, setMessages] = useState<ClientSideMessages[]>(chatStart ? [
        {
            messageContent: chatStart?.messageContent || '',
            messageOwnerName: chatStart?.messageSenderName || '',
            messageDateUtc: chatStart?.messageDateUtc,
            messageSide: MessageSideEnum.ClientUser
        }
    ] : []);
    const [textareaRows, setTextareaRows] = useState(1);
    const [newMessage, setNewMessage] = useState('');  // Mesaj girişi için state
    const [sessionEnded, setSessionEnded] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false); // Kullanıcı yazıyor mu?


    const query = new URLSearchParams(useLocation().search);
    const guid = query.get('guid');
    const ServiceId = query.get('hId');

    const isChatStarted = useRef(false);

    const startChat = async () => {
        if (isChatStarted.current) {
            return;
        }

        setLoading(true);
        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(GlobalOperations.SignalRBaseUrl + "/signalr", {
                transport: signalR.HttpTransportType.WebSockets
            })
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect([0, 2000, 10000, 30000]) // Custom reconnect intervals
            .build();

        newConnection.onclose(async () => {
            console.log("Connection lost. Attempting to reconnect...");
            try {
                await newConnection.start();
                console.log("Reconnected!");
            } catch (err) {
                console.error("Failed to reconnect:", err);
            }
        });


        await newConnection.start();
        console.log("SignalR bağlantısı kuruldu");
        const id = await newConnection.invoke<string>("GetConnectionId");
        GlobalOperations.ConnectionHubId = id;
        GlobalOperations.SignalRConnection = newConnection;

        if (GlobalOperations.SignalRConnection && GlobalOperations.ConnectionHubId && chatStart) {
            setClientUserName(chatStart.clientName);
            let request: SetSignalRSessionToChatSessionRequest = {
                serviceGuid: guid || '',
                serviceId: chatStart.serviceId || 0,
                sessionId: chatStart.sessionId || 0,
                signalRConnectionId: GlobalOperations.ConnectionHubId
            };

            let response: ApiBaseResponse<SetSignalRSessionToChatSessionResponse> | null =
                await SetSignalRSessionToChatSession(request);

            if (!response || !response.content || response.responseCode !== ResponseCode.OK) {
                return;
            }

            const startChatRequest = {
                serviceGuid: response.content.serviceGuid,
                serviceId: response.content.serviceId,
                sessionId: response.content.sessionId,
                connectionId: GlobalOperations.ConnectionHubId,
                hubList: response.content.hubList,
                clientName: response.content.clientName,
                clientId: response.content.clientId
            };

            setSessionId(response.content.sessionId);

            try {
                let response: boolean =
                    await GlobalOperations.SignalRConnection.invoke("startChatFromClient", startChatRequest);

                if (!response) {
                    return;
                }

                console.log("SignalR ile startChatFromClient çağrısı başarılı.");
                isChatStarted.current = true;
            } catch (error) {
                console.error('SignalR ile startChatFromClient çağrısı yapılamadı:', error);
                return;
            }
        } else {
            console.error("SignalR bağlantısı aktif değil. Bağlantı bekleniyor...");
        }

        setLoading(false);
    };

    useEffect(() => {
        if (!isChatStarted.current) {
            startChat();
        }
    }, [chatStart, guid]);

    useEffect(() => {
        if (GlobalOperations.SignalRConnection && GlobalOperations.ConnectionHubId) {
            GlobalOperations.SignalRConnection.on('receiveMessageByAgent', (session: SendMessageByAgentResponse) => {
                if (sessionId !== session?.sessionId) return;

                const newAgentMessage: ClientSideMessages = {
                    messageContent: session?.agentMessage || '',
                    messageOwnerName: session?.agentName || '',
                    messageDateUtc: session.messageDateUtc,
                    messageSide: MessageSideEnum.Agent
                };

                setMessages(prevMessages => [...prevMessages, newAgentMessage]);
                //setShowExportPdf(true); // TODO ilerleyein dönemde açılabilir. raporu pdf e aktarma özelliği
            });

            GlobalOperations.SignalRConnection.on('endSession4Client', (session: SendMessageByAgentResponse) => {
                GlobalOperations.ActiveSessionId = 0;
                // Oturum sonlandığında bu state'i true yapıyoruz.
                setSessionEnded(true);
            });

            GlobalOperations.SignalRConnection.on('changeAgentTyping4Client', (session: ChangeAgentTypingResponse) => {
                // Eğer agent yazıyorsa isTyping'i true yap, değilse false yap
                setIsTyping(session.isTyping);

            });

            return () => {
                if (GlobalOperations.SignalRConnection != null) {
                    GlobalOperations.SignalRConnection.off('receiveMessageByAgent');
                    GlobalOperations.SignalRConnection.off('endSession4Client');
                    GlobalOperations.SignalRConnection.off('changeAgentTyping4Client');
                }
            };
        }
    }, [GlobalOperations.SignalRConnection, sessionId]);

    const handleNewMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newMessage.trim()) {
            sendMessageOverSignalR(newMessage);
        }
    };

    const sendMessageOverSignalR = async (message: string) => {

        message = message?.trim();
        let request: SendMessageByClientUserRequest = {
            messageContent: message,
            serviceGuid: guid || '',
            serviceId: Number(ServiceId),
            sessionId: Number(sessionId)
        };

        if (GlobalOperations.SignalRConnection == null) {
            console.error("SignalR bağlantısı yok.");
            return;
        }

        let response: boolean = await GlobalOperations.SignalRConnection.invoke("sendMessageByClientUser", request);
        if (!response) return;

        const newClientMessage: ClientSideMessages = {
            messageContent: message,
            messageOwnerName: clientUserName,
            messageDateUtc: Date.now(),
            messageSide: MessageSideEnum.ClientUser
        };

        setMessages([...messages, newClientMessage]);
        setNewMessage('');  // Mesaj gönderildikten sonra text alanı boşaltılıyor
        setTextareaRows(1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleNewMessage(e as any);
        } else if (e.key === 'Enter' && e.shiftKey) {
            setTextareaRows(prevRows => prevRows + 1);
        }
    };

    const handleIconClick = () => {
        handleNewMessage({ preventDefault: () => { } } as React.FormEvent);
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const generatePdf = async () => {
        const input = chatContentRef.current;
        if (!input) return;

        // Tüm içeriği yakalayarak bir canvas oluşturuyoruz
        const canvas = await html2canvas(input, {
            scrollX: 0,
            scrollY: 0, // Sayfanın tam üstünden yakalama
            width: input.scrollWidth,  // Tüm genişlik
            height: input.scrollHeight // Tüm yüksekliği yakalama
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4'); // PDF oluşturma
        const pageHeight = pdf.internal.pageSize.height; // PDF sayfası yüksekliği
        const pageWidth = pdf.internal.pageSize.width;  // PDF sayfası genişliği
        const imgWidth = pageWidth - 20; // Görüntü PDF'de kaplayacak genişlik
        const imgHeight = (canvas.height * imgWidth) / canvas.width; // Oranlı yükseklik

        let heightLeft = imgHeight; // Kalan yükseklik
        let position = 0; // İlk sayfanın başlangıç pozisyonu

        // İlk sayfayı ekliyoruz
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Daha fazla içerik varsa, yeni sayfalar ekliyoruz
        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage(); // Yeni sayfa ekleme
            pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight); // Yeni sayfa içeriği
            heightLeft -= pageHeight;
        }

        // PDF'yi kaydediyoruz
        pdf.save('chat.pdf');
    };

    useEffect(() => {
        if (isTyping && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isTyping]);



    const linkify = (text: string) => {
        // Hem http, https ile başlayan, hem de www. ile başlayan linkleri yakalamak için regex
        const urlRegex = /(\b(https?:\/\/|www\.)[^\s]+)/g;

        return text.replace(urlRegex, (url) => {
            // Eğer link www. ile başlıyorsa, başına http:// ekle
            if (url.startsWith('www.')) {
                return `<a href="http://${url}" target="_blank" style="color: inherit; text-decoration: underline;">${url}</a>`;
            }

            // Eğer http veya https ile başlıyorsa, direkt a tagına dönüştür
            return `<a href="${url}" target="_blank" style="color: inherit; text-decoration: underline;">${url}</a>`;
        });
    };


    const allowedDocumentTypes = [
        // PDF
        'application/pdf',

        // Word Documents
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx

        // Excel Files
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx

        // Image Files
        'image/jpeg', // .jpg, .jpeg
        'image/png',  // .png
        'image/gif'   // .gif
    ];


    const handleSuccess = (response: FileUploadResponse) => {
        const fileUrl = `${GlobalOperations.BaseUrl}${response.uploadedFileUrl}`;

        // 'fileUrl:' formatında mesaj oluşturma
        const messageContent = `fileUrl:'${fileUrl}'`;

        // Bu bileşeni SignalR ile diğer kullanıcılara mesaj olarak gönder
        sendMessageOverSignalR(messageContent);
    };




    const handleError = (error: string) => {
        alert('Yükleme hatası: ' + error);
    };



    return (
        <div
            style={{
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '2px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
            }}
        >
            {loading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    backgroundColor: 'white'
                }}>
                    <h1>Yükleniyor...</h1>
                </div>
            ) : sessionEnded ? (  // Oturum sonlandığında gösterilecek mesaj
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',   // Yatayda ortalama
                    alignItems: 'center',       // Dikeyde ortalama
                    height: '100vh',            // Tam ekran yüksekliği
                    backgroundColor: 'white',
                    flexDirection: 'column',    // İçerikleri üst üste getirme
                    textAlign: 'center'         // Metni ortalama (isteğe bağlı)
                }}>
                    <h1>Oturum sonlanmıştır</h1>
                    <p>Yeniden oturum oluşturmak için sayfayı yenileyiniz.</p>
                </div>

            ) : (
                <>
                    <div style={{ padding: '10px', backgroundColor: 'white', borderBottom: '1px solid #ddd' }}>
                        <strong>{messages[0]?.messageOwnerName}</strong>{' '}
                        <span style={{ color: '#888', fontSize: '12px' }}>{new Date(messages[0]?.messageDateUtc).toLocaleTimeString()}</span>
                    </div>

                    <div ref={chatContentRef} style={{ flexGrow: 1, backgroundColor: '#f0f0f0', overflowY: 'auto', padding: '10px' }}>
                        {messages.map((msg, index) => {
                            const isFileMessage = msg.messageContent.startsWith('fileUrl:'); // fileUrl ile mi başlıyor?

                            return (
                                <div
                                    key={index}
                                    style={{
                                        textAlign: msg.messageSide === MessageSideEnum.ClientUser ? 'right' : 'left',
                                        margin: msg.messageSide === MessageSideEnum.ClientUser ? '1rem 1rem 1rem auto' : '1rem auto 1rem 1rem',
                                        padding: '10px',
                                        borderRadius: msg.messageSide === MessageSideEnum.ClientUser ? '1.125rem 1.125rem 0 1.125rem' : '1.125rem 1.125rem 1.125rem 0',
                                        backgroundColor: msg.messageSide === MessageSideEnum.ClientUser ? '#2980b9' : '#333',
                                        color: 'white',
                                        display: 'block',
                                        maxWidth: '80%',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {/* Eğer mesaj fileUrl ile başlıyorsa RenderMessageContent ile göster */}
                                    {isFileMessage ? (
                                        // Dosya URL'sini msg.messageContent'ten çıkaralım
                                        <RenderMessageContent url={msg.messageContent.replace('fileUrl:', '')} />
                                    ) : (
                                        // Normal mesajlar için
                                        <div dangerouslySetInnerHTML={{ __html: linkify(msg.messageContent) }} />
                                    )}

                                    {/* Mesaj zamanı */}
                                    <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
                                        {new Date(msg.messageDateUtc).toLocaleString()}
                                    </div>
                                </div>
                            );
                        })}


                        {/* Eğer isTyping true ise yazıyor bubble'ı göstereceğiz */}
                        {isTyping && (
                            <div style={{
                                textAlign: 'left',
                                margin: '1rem auto 1rem 1rem',
                                padding: '10px',
                                borderRadius: '1.125rem 1.125rem 1.125rem 0',
                                backgroundColor: '#ccc',
                                color: 'black',
                                display: 'inline-block',
                                maxWidth: '80%',
                                fontStyle: 'italic',
                            }}>
                                Yazıyor...
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <div style={{
                        padding: '10px',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center', // Öğeleri dikey olarak ortalayacak
                        borderTop: '1px solid #ddd'
                    }}>
                        <FileUploadComponent
                            uploadUrl={GlobalOperations.BaseUrl + "/api/Portal/UploadClientUserFile"}
                            allowedTypes={allowedDocumentTypes}
                            maxSizeMB={10} // Maksimum 10 MB doküman
                            onSuccess={handleSuccess} // response parametresi ile çağırılıyor
                            onError={handleError}
                            hID={ServiceId || ''}                  // hID parametresi
                            guid={guid || ''}              // guid parametresi
                            sessionID={sessionId}     // sessionID parametresi
                            icon={<FaCamera size={32} />}  // Font Awesome ikonu
                            customStyle={{
                                fontSize: '20px',
                                color: '#2980b9',
                                cursor: 'pointer',
                                marginRight: '10px',
                                flexShrink: 0,
                                height: '10px',
                                width: '10px',
                                padding: '20px',
                                textAlign: 'center',
                                display: 'flex',             // Flexbox ile ortalama
                                alignItems: 'center',        // Dikey ortalama
                                justifyContent: 'center',    // Yatay ortalama                                
                            }}
                        />
                        <TextArea
                            ref={messageRef}
                            inputId="newMessage"
                            placeholder="Mesajınızı yazın..."
                            rows={textareaRows}
                            style={{
                                flexGrow: 1,
                                padding: '8px',
                                borderRadius: '8px',
                                border: '1px solid #ccc',
                                marginRight: '10px',
                                display: 'block' // Daha iyi hizalama için block display
                            }}
                            onKeyDown={handleKeyDown}
                            label={''}
                            value={newMessage}
                            onChange={(value: string) => setNewMessage(value)}
                            showHeader={false}
                        />
                        <FaPaperPlane
                            onClick={handleIconClick}
                            style={{
                                fontSize: '20px',
                                color: '#2980b9',
                                cursor: 'pointer',
                                flexShrink: 0,
                                marginRight: '10px',
                                marginLeft: '10px',
                            }}
                        />
                        {showExportPdf && (
                            <FaFilePdf
                                data-tip="Konuşmayı PDF'e aktar"
                                data-for="pdfTip"
                                onClick={generatePdf}
                                style={{
                                    fontSize: '20px',
                                    color: '#2980b9',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    marginLeft: '10px'
                                }}
                            />
                        )}
                    </div>

                </>
            )}
        </div>
    );
};

export default ChatScreen;
